import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import axios from 'axios';
import moment from 'moment';
import './Holidays.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Holidays = () => {
  const [holidays, setHolidays] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  const fetchHolidays = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/holidays`, {
        params: { year: selectedYear }
      });
      
      if (response.data.success) {
        setHolidays(response.data.holidays);
      }
    } catch (err) {
      console.error('Error fetching holidays:', err);
      setError('Failed to load holidays. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHolidays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  const getHolidayIcon = (type) => {
    switch (type) {
      case 'federal':
        return '🇨🇦';
      case 'provincial':
        return '🇨🇦';
      case 'company':
        return '🏢';
      default:
        return '📅';
    }
  };

  const getHolidayForDate = (date) => {
    const dateStr = moment(date).format('YYYY-MM-DD');
    return holidays.find(h => h.date === dateStr);
  };

  const generateCalendarMonths = () => {
    const months = [];
    for (let month = 0; month < 12; month++) {
      const firstDay = moment({ year: selectedYear, month, day: 1 });
      const daysInMonth = firstDay.daysInMonth();
      const startDayOfWeek = firstDay.day();
      
      const weeks = [];
      let currentWeek = new Array(startDayOfWeek).fill(null);
      
      for (let day = 1; day <= daysInMonth; day++) {
        const date = moment({ year: selectedYear, month, day });
        currentWeek.push({
          date: date.toDate(),
          day,
          holiday: getHolidayForDate(date)
        });
        
        if (currentWeek.length === 7) {
          weeks.push(currentWeek);
          currentWeek = [];
        }
      }
      
      if (currentWeek.length > 0) {
        while (currentWeek.length < 7) {
          currentWeek.push(null);
        }
        weeks.push(currentWeek);
      }
      
      months.push({
        name: firstDay.format('MMMM'),
        weeks
      });
    }
    return months;
  };

  const calendarMonths = generateCalendarMonths();
  const availableYears = [2025, 2026, 2027];
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <Layout>
      <div className="holidays-container">
        <div className="holidays-header">
          <div className="holidays-title-section">
            <h1>🎉 Holiday Calendar</h1>
            <p className="holidays-subtitle">
              {user?.isFullTime !== false 
                ? 'Company holidays are not deducted from your PTO balance as a full-time employee.'
                : 'View company holidays for the year.'}
            </p>
          </div>
          <div className="holidays-controls">
            <select
              className="year-selector"
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            >
              {availableYears.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading && (
          <div className="loading-message">
            <div className="spinner"></div>
            <p>Loading holidays...</p>
          </div>
        )}

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        {!loading && !error && holidays.length === 0 && (
          <div className="empty-state">
            <span className="empty-icon">📅</span>
            <h3>No Holidays Found</h3>
            <p>There are no holidays scheduled for {selectedYear}.</p>
          </div>
        )}

        {!loading && !error && holidays.length > 0 && (
          <div className="calendar-grid">
            {calendarMonths.map((monthData, idx) => (
              <div key={idx} className="calendar-month">
                <h2 className="calendar-month-header">{monthData.name}</h2>
                <div className="calendar-table">
                  <div className="calendar-weekdays">
                    {weekDays.map(day => (
                      <div key={day} className="calendar-weekday">{day}</div>
                    ))}
                  </div>
                  <div className="calendar-days">
                    {monthData.weeks.map((week, weekIdx) => (
                      <div key={weekIdx} className="calendar-week">
                        {week.map((dayData, dayIdx) => (
                          <div
                            key={dayIdx}
                            className={`calendar-day ${dayData ? '' : 'empty'} ${dayData?.holiday ? 'holiday' : ''}`}
                          >
                            {dayData && (
                              <>
                                <div className="day-number">{dayData.day}</div>
                                {dayData.holiday && (
                                  <div className="holiday-indicator">
                                    <div className="holiday-flag">{getHolidayIcon(dayData.holiday.type)}</div>
                                    <div className="holiday-name-tooltip">{dayData.holiday.name}</div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="holidays-info-section">
          <div className="info-card">
            <h3>📌 About Holidays</h3>
            <ul>
              <li>
                <strong>Federal Holidays:</strong> Official Canadian federal holidays observed by the company.
              </li>
              <li>
                <strong>Provincial Holidays:</strong> Provincial holidays that may vary by location.
              </li>
              <li>
                <strong>Company Holidays:</strong> Additional holidays provided by the company.
              </li>
              <li>
                <strong>Full-Time Employees:</strong> Holidays are not deducted from your PTO balance.
              </li>
              <li>
                <strong>Part-Time Employees:</strong> Holiday policy may vary based on your employment status.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Holidays;
